import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Input.css';

/**
 * Input Component
 * 
 * A flexible text input component with validation states, icons, and accessibility.
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Input type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search'
 * @param {string} props.value - Input value (controlled)
 * @param {string} props.defaultValue - Default value (uncontrolled)
 * @param {function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.readOnly - Read-only state
 * @param {boolean} props.required - Required field
 * @param {string} props.size - Input size: 'sm' | 'md' | 'lg'
 * @param {string} props.variant - Visual state: 'default' | 'error' | 'success' | 'warning'
 * @param {React.ReactNode} props.iconLeft - Icon on left side
 * @param {React.ReactNode} props.iconRight - Icon on right side
 * @param {boolean} props.fullWidth - Full width input
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input id attribute
 * @param {string} props.autoComplete - Autocomplete attribute
 * @param {number} props.maxLength - Maximum length
 * @param {number} props.minLength - Minimum length
 * @param {string} props.pattern - Validation pattern
 * @param {string} props.className - Additional CSS classes
 */
const Input = forwardRef(({
  type = 'text',
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  placeholder = '',
  disabled = false,
  readOnly = false,
  required = false,
  size = 'md',
  variant = 'default',
  iconLeft = null,
  iconRight = null,
  fullWidth = false,
  name,
  id,
  autoComplete,
  maxLength,
  minLength,
  pattern,
  className = '',
  ...props
}, ref) => {
  const containerClasses = [
    'input-container',
    `input-container--${size}`,
    `input-container--${variant}`,
    fullWidth ? 'input-container--full-width' : '',
    disabled ? 'input-container--disabled' : '',
    iconLeft ? 'input-container--has-left-icon' : '',
    iconRight ? 'input-container--has-right-icon' : '',
    className
  ].filter(Boolean).join(' ');

  const inputClasses = [
    'input',
    `input--${size}`,
    iconLeft ? 'input--with-left-icon' : '',
    iconRight ? 'input--with-right-icon' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {iconLeft && (
        <span className="input__icon input__icon--left" aria-hidden="true">
          {iconLeft}
        </span>
      )}
      
      <input
        ref={ref}
        type={type}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        name={name}
        id={id}
        autoComplete={autoComplete}
        maxLength={maxLength}
        minLength={minLength}
        pattern={pattern}
        className={inputClasses}
        aria-invalid={variant === 'error' ? 'true' : undefined}
        aria-required={required ? 'true' : undefined}
        {...props}
      />
      
      {iconRight && (
        <span className="input__icon input__icon--right" aria-hidden="true">
          {iconRight}
        </span>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  type: PropTypes.oneOf(['text', 'email', 'password', 'number', 'tel', 'url', 'search']),
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['default', 'error', 'success', 'warning']),
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  fullWidth: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  autoComplete: PropTypes.string,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  pattern: PropTypes.string,
  className: PropTypes.string,
};

export default Input;
